import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { Plus, Folder, Calendar, Trash2, Edit2, Users, History, CheckCircle, Square, CheckSquare, X, Filter, Search, Film, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Projects = () => {
  const { getAuthHeaders, user } = useAuth();
  const confirm = useConfirm();
  const [projects, setProjects] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [staffUsers, setStaffUsers] = useState({ roteiristas: [], mixadores: [], srt: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [clientFilter, setClientFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('recent'); // 'recent' | 'oldest' | 'az' | 'za'
  const [formData, setFormData] = useState({
    title: '', description: '', client: '', project_type: 'PROJETO COMPLETO',
    delivery_deadline: '', demo_deadline: '', final_deadline: '', drive_folder_link: '', drive_upload_folder_link: '',
    duration_display: '', episodes: '',
    responsible_admin_ids: [], roteiristas_ids: [], srt_ids: [], mixadores_ids: []
  });

  const isAdmin = user?.role === 'dono' || user?.role === 'administrador';
  const canCreate = user?.role === 'dono' || user?.role === 'administrador' || user?.role === 'mixador';

  useEffect(() => { fetchProjects(); if (isAdmin) fetchAdmins(); }, [showHistory]);

  const fetchProjects = async () => {
    try {
      const filter = showHistory ? 'history' : 'active';
      const response = await axios.get(`${API}/projects?status_filter=${filter}`, { headers: getAuthHeaders() });
      setProjects(response.data);
      setSelected(new Set());
    } catch { toast.error('Erro ao carregar projetos'); }
    finally { setLoading(false); }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
      const all = response.data || [];
      // Hierarquia: DONO > ADM > MIXADOR > ROTEIRISTA > DUBLADOR.
      // Cada lista inclui o próprio cargo + todos os superiores.
      // Ordenação: alfabética por nome artístico (ou nome) — pt-BR, case-insensitive.
      const inRoles = (u, roles) => roles.includes(u.role);
      const byName = (a, b) =>
        (a.artist_name || a.name || '').localeCompare(b.artist_name || b.name || '', 'pt-BR', { sensitivity: 'base' });
      const sortAlpha = (arr) => [...arr].sort(byName);

      setAdmins(sortAlpha(all.filter(u => inRoles(u, ['dono', 'administrador']))));
      setStaffUsers({
        roteiristas: sortAlpha(all.filter(u => inRoles(u, ['roteirista', 'mixador', 'administrador', 'dono']))),
        mixadores:   sortAlpha(all.filter(u => inRoles(u, ['mixador', 'administrador', 'dono']))),
        srt:         sortAlpha(all.filter(u => inRoles(u, ['roteirista', 'mixador', 'administrador', 'dono']))),
      });
    } catch (err) { console.error('Failed to fetch admins:', err); }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', client: '', project_type: 'PROJETO COMPLETO',
      delivery_deadline: '', demo_deadline: '', final_deadline: '', drive_folder_link: '', drive_upload_folder_link: '',
      duration_display: '', episodes: '',
      responsible_admin_ids: [], roteiristas_ids: [], srt_ids: [], mixadores_ids: []
    });
    setEditingProject(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        if (Array.isArray(val)) { data.append(key, JSON.stringify(val)); }
        else if (val) { data.append(key, val); }
      });
      await axios.post(`${API}/projects`, data, { headers: getAuthHeaders() });
      toast.success('Projeto criado com sucesso!');
      setShowModal(false); resetForm(); fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao criar projeto'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        if (Array.isArray(val)) { data.append(key, JSON.stringify(val)); }
        else { data.append(key, val || ''); }
      });
      await axios.put(`${API}/projects/${editingProject.id}`, data, { headers: getAuthHeaders() });
      toast.success('Projeto atualizado!');
      setShowModal(false); resetForm(); fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao atualizar projeto'); }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '', description: project.description || '', client: project.client || '',
      project_type: project.project_type || 'PROJETO COMPLETO',
      delivery_deadline: project.delivery_deadline ? project.delivery_deadline.split('T')[0] : '',
      demo_deadline: project.demo_deadline ? project.demo_deadline.split('T')[0] : '',
      final_deadline: project.final_deadline ? project.final_deadline.split('T')[0] : '',
      drive_folder_link: project.drive_folder_link || '',
      drive_upload_folder_link: project.drive_upload_folder_link || '',
      duration_display: project.duration_display || '',
      episodes: project.episodes != null ? String(project.episodes) : '',
      responsible_admin_ids: project.responsible_admin_ids || (project.responsible_admin_id ? [project.responsible_admin_id] : []),
      roteiristas_ids: project.roteiristas_ids || [],
      srt_ids: project.srt_ids || [],
      mixadores_ids: project.mixadores_ids || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    const ok = await confirm('Tem certeza que deseja excluir este projeto? Todos os personagens e arquivos serão perdidos.');
    if (!ok) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: getAuthHeaders() });
      toast.success('Projeto excluído!'); fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao excluir projeto'); }
  };

  const handleComplete = async (projectId) => {
    const ok = await confirm('Marcar este projeto como concluído?');
    if (!ok) return;
    try {
      const data = new FormData(); data.append('status', 'Concluído');
      await axios.put(`${API}/projects/${projectId}`, data, { headers: getAuthHeaders() });
      toast.success('Projeto concluído!'); fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao concluir projeto'); }
  };

  const handleReopen = async (projectId) => {
    try {
      const data = new FormData(); data.append('status', 'Em Andamento');
      await axios.put(`${API}/projects/${projectId}`, data, { headers: getAuthHeaders() });
      toast.success('Projeto reaberto!'); fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao reabrir projeto'); }
  };

  // Bulk actions
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredProjects.length) setSelected(new Set());
    else setSelected(new Set(filteredProjects.map(p => p.id)));
  };

  const handleBulkDelete = async () => {
    const ok = await confirm(`Excluir ${selected.size} projeto(s) selecionado(s)?`);
    if (!ok) return;
    try {
      await axios.post(`${API}/projects/bulk-delete`, [...selected], { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`${selected.size} projetos excluídos!`); fetchProjects();
    } catch { toast.error('Erro ao excluir projetos'); }
  };

  const handleBulkComplete = async () => {
    const ok = await confirm(`Concluir ${selected.size} projeto(s) selecionado(s)?`);
    if (!ok) return;
    try {
      await axios.post(`${API}/projects/bulk-complete`, [...selected], { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`${selected.size} projetos concluídos!`); fetchProjects();
    } catch { toast.error('Erro ao concluir projetos'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('T')[0].split('-');
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    } catch { return dateStr; }
  };

  const getStatusBadge = (project) => {
    if (project.status === 'Concluído') return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">CONCLUÍDO</span>;
    if (project.characters_late > 0) return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">ATRASADO</span>;
    if (project.characters_pending > 0) return <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">PENDENTE</span>;
    if (project.characters_delivered > 0) return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">EM DIA</span>;
    return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">{project.status}</span>;
  };

  // Get unique values for filters
  const uniqueClients = [...new Set(projects.map(p => p.client).filter(Boolean))].sort();
  const uniqueTypes = [...new Set(projects.map(p => p.project_type).filter(Boolean))].sort();
  const uniqueResponsibles = [...new Set(projects.flatMap(p => p.responsible_admin_names || (p.responsible_admin_name ? [p.responsible_admin_name] : [])))].sort();

  // Filter + sort projects
  const norm = (s) => s?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const filteredProjects = projects.filter(p => {
    if (clientFilter && p.client !== clientFilter) return false;
    if (typeFilter && p.project_type !== typeFilter) return false;
    if (responsibleFilter) {
      const names = p.responsible_admin_names || (p.responsible_admin_name ? [p.responsible_admin_name] : []);
      if (!names.includes(responsibleFilter)) return false;
    }
    if (searchQuery) {
      const q = norm(searchQuery);
      if (!norm(p.title).includes(q) && !norm(p.client).includes(q) && !norm(p.description).includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortOrder === 'az') return (a.title || '').localeCompare(b.title || '', 'pt-BR');
    if (sortOrder === 'za') return (b.title || '').localeCompare(a.title || '', 'pt-BR');
    if (sortOrder === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '');
    return (b.created_at || '').localeCompare(a.created_at || ''); // recent
  });

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="projects-page">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            {showHistory ? 'Histórico de Projetos' : 'Projetos'}
          </h1>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setShowHistory(!showHistory)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showHistory ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
              data-testid="history-btn"
            >
              <History className="h-5 w-5" />
              {showHistory ? 'Ver Ativos' : 'Histórico'}
            </button>
            {canCreate && !showHistory && (
              <button onClick={() => { resetForm(); setShowModal(true); }}
                className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors"
                data-testid="new-project-btn"
              >
                <Plus className="h-5 w-5" /> Novo Projeto
              </button>
            )}
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar projeto..." className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-8 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="project-search" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
          </div>

          {/* Client Filter */}
          <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="client-filter-select">
            <option value="">Todos Clientes</option>
            {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Type Filter */}
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="type-filter-select">
            <option value="">Todos Tipos</option>
            {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {/* Responsible Filter */}
          {uniqueResponsibles.length > 0 && (
            <select value={responsibleFilter} onChange={e => setResponsibleFilter(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="responsible-filter-select">
              <option value="">Todos Responsáveis</option>
              {uniqueResponsibles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}

          {/* Sort */}
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="sort-select">
            <option value="recent">Mais Recente</option>
            <option value="oldest">Mais Antigo</option>
            <option value="az">Nome A-Z</option>
            <option value="za">Nome Z-A</option>
          </select>

          {/* Clear filters */}
          {(clientFilter || typeFilter || responsibleFilter || searchQuery) && (
            <button onClick={() => { setClientFilter(''); setTypeFilter(''); setResponsibleFilter(''); setSearchQuery(''); }}
              className="flex items-center gap-1 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors" data-testid="clear-filters-btn">
              <X className="h-4 w-4" /> Limpar
            </button>
          )}
        </div>

        {/* Bulk Action Bar */}
        {isAdmin && selected.size > 0 && (
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl px-5 py-3 flex items-center justify-between" data-testid="bulk-action-bar">
            <span className="text-teal-400 font-medium text-sm">{selected.size} selecionado(s)</span>
            <div className="flex gap-2">
              {!showHistory && (
                <button onClick={handleBulkComplete} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-colors" data-testid="bulk-complete-btn">
                  <CheckCircle className="h-3.5 w-3.5" /> Concluir
                </button>
              )}
              <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors" data-testid="bulk-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Excluir
              </button>
              <button onClick={() => setSelected(new Set())} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Select All */}
        {isAdmin && filteredProjects.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="select-all-btn">
              {selected.size === filteredProjects.length ? <CheckSquare className="h-4 w-4 text-teal-400" /> : <Square className="h-4 w-4" />}
              {selected.size === filteredProjects.length ? 'Desmarcar todos' : 'Selecionar todos'}
            </button>
          </div>
        )}

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map(project => (
            <div key={project.id}
              className={`bg-slate-800/50 rounded-xl border transition-all ${selected.has(project.id) ? 'border-teal-500 ring-1 ring-teal-500/30' : 'border-slate-700 hover:border-teal-500/50'}`}
              data-testid={`project-card-${project.id}`}
            >
              <div className="flex items-start">
                {isAdmin && (
                  <button onClick={() => toggleSelect(project.id)} className="p-4 pr-0 shrink-0" data-testid={`select-project-${project.id}`}>
                    {selected.has(project.id) ? <CheckSquare className="h-5 w-5 text-teal-400" /> : <Square className="h-5 w-5 text-slate-600 hover:text-slate-400" />}
                  </button>
                )}
                <Link to={`/projects/${project.id}`} className="block p-6 pb-3 flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-teal-500/20 p-3 rounded-lg shrink-0">
                      <Folder className="h-6 w-6 text-teal-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-white hover:text-teal-400 transition-colors truncate">{project.title}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {getStatusBadge(project)}
                        <span className="text-xs text-slate-500">{project.project_type}</span>
                      </div>
                    </div>
                  </div>
                  {project.client && <p className="text-slate-400 text-sm mb-2">Cliente: {project.client}</p>}
                  {(project.responsible_admin_names || [project.responsible_admin_name]).filter(Boolean).length > 0 && (
                    <p className="text-teal-400 text-sm mb-2">Responsável: {(project.responsible_admin_names || [project.responsible_admin_name]).filter(Boolean).join(', ')}</p>
                  )}
                  {project.description && <p className="text-slate-500 text-sm mb-3 line-clamp-2">{project.description}</p>}
                  {project.pending_replacements > 0 && (
                    <div
                      className="flex items-center gap-1.5 text-xs font-medium text-red-300 bg-red-500/15 border border-red-500/30 rounded-md px-2 py-1 mb-2 w-fit"
                      data-testid={`replacement-badge-${project.id}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                      {project.pending_replacements} substituição(ões) pendente(s)
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm text-slate-400 pt-3 border-t border-slate-700 flex-wrap gap-y-1.5">
                    <div className="flex items-center gap-1" title="Personagens entregues / total" data-testid={`char-count-${project.id}`}>
                      <Users className="h-4 w-4" />
                      {(project.characters_delivered || 0)}/{project.characters_count || 0}
                    </div>
                    {(project.episodes > 0) && (
                      <div className="flex items-center gap-1" data-testid={`episodes-${project.id}`}>
                        <Film className="h-4 w-4" />{project.episodes} ep{project.episodes !== 1 ? 's' : ''}
                      </div>
                    )}
                    {project.duration_display && (
                      <div className="flex items-center gap-1 font-mono" data-testid={`duration-${project.id}`}>
                        <Clock className="h-4 w-4" />{project.duration_display}
                      </div>
                    )}
                    {project.delivery_deadline && <div className="flex items-center gap-1"><Calendar className="h-4 w-4" />{formatDate(project.delivery_deadline)}</div>}
                  </div>
                </Link>
              </div>

              {isAdmin && (
                <div className="flex gap-2 px-6 pb-4 pt-2 border-t border-slate-700/50">
                  {!showHistory && project.status !== 'Concluído' && (
                    <button onClick={() => handleComplete(project.id)}
                      className="flex items-center gap-1.5 text-green-400 hover:text-green-300 px-3 py-1.5 rounded-lg hover:bg-green-500/10 transition-colors text-xs font-medium"
                      data-testid={`complete-project-${project.id}`}>
                      <CheckCircle className="h-3.5 w-3.5" /> Concluir
                    </button>
                  )}
                  {showHistory && (
                    <button onClick={() => handleReopen(project.id)}
                      className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-500/10 transition-colors text-xs font-medium">
                      <History className="h-3.5 w-3.5" /> Reabrir
                    </button>
                  )}
                  <button onClick={() => handleEdit(project)}
                    className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-500/10 transition-colors text-xs font-medium"
                    data-testid={`edit-project-${project.id}`}>
                    <Edit2 className="h-3.5 w-3.5" /> Editar
                  </button>
                  <button onClick={() => handleDelete(project.id)}
                    className="flex items-center gap-1.5 text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg hover:bg-red-500/10 transition-colors text-xs font-medium ml-auto"
                    data-testid={`delete-project-${project.id}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Excluir
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <Folder className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">{clientFilter ? `Nenhum projeto do cliente "${clientFilter}".` : showHistory ? 'Nenhum projeto no histórico.' : 'Nenhum projeto ativo.'}</p>
            {clientFilter && <button onClick={() => setClientFilter('')} className="text-teal-400 hover:text-teal-300 text-sm mt-2">Limpar filtro</button>}
          </div>
        )}

        {/* Create/Edit Project Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 my-auto max-h-[calc(100vh-2rem)] overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-6">{editingProject ? 'Editar Projeto' : 'Novo Projeto'}</h2>
              <form onSubmit={editingProject ? handleUpdate : handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Nome do Projeto *</label>
                  <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" required data-testid="project-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Cliente</label>
                    <input type="text" value={formData.client} onChange={e => setFormData({...formData, client: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" placeholder="Nome do cliente" data-testid="project-client-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Tipo</label>
                    <select value={formData.project_type} onChange={e => setFormData({...formData, project_type: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" data-testid="project-type-select">
                      <option value="PROJETO COMPLETO">PROJETO COMPLETO</option>
                      <option value="EP11 EM DIANTE">EP11 EM DIANTE</option>
                    </select>
                  </div>
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Responsáveis</label>
                    <div className="space-y-1 max-h-32 overflow-y-auto bg-slate-700 border border-slate-600 rounded-lg p-2">
                      {admins.map(admin => {
                        const displayName = admin.artist_name || admin.name;
                        return (
                          <label key={admin.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-600 rounded cursor-pointer text-sm">
                            <input type="checkbox" className="accent-teal-500 shrink-0"
                              checked={(formData.responsible_admin_ids || []).includes(admin.id)}
                              onChange={(e) => {
                                const ids = formData.responsible_admin_ids || [];
                                setFormData({...formData, responsible_admin_ids: e.target.checked ? [...ids, admin.id] : ids.filter(id => id !== admin.id)});
                              }} />
                            <span className="text-white truncate flex-1" title={displayName}>{displayName}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Duração do projeto + Número de episódios */}
                {isAdmin && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        Duração do Projeto <span className="text-slate-500 text-xs">(HH:MM:SS ou MMM,SS)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.duration_display}
                        onChange={e => setFormData({...formData, duration_display: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500 font-mono"
                        placeholder="00:00:00 ou 107,24"
                        data-testid="project-duration-input"
                      />
                      {formData.duration_display && (() => {
                        // Support both formats: HH:MM:SS and MMM,SS
                        const val = formData.duration_display.trim();
                        let totalSec = 0;
                        const colon = /^(\d{1,3}):(\d{1,2}):(\d{1,2})$/.exec(val);
                        const comma = /^(\d{1,5}),(\d{1,2})$/.exec(val);
                        if (colon) totalSec = Number(colon[1]) * 3600 + Number(colon[2]) * 60 + Number(colon[3]);
                        else if (comma) totalSec = Number(comma[1]) * 60 + Number(comma[2]);
                        else return null;
                        const mins = (totalSec / 60).toFixed(2);
                        const hms = `${String(Math.floor(totalSec / 3600)).padStart(2,'0')}:${String(Math.floor((totalSec % 3600) / 60)).padStart(2,'0')}:${String(totalSec % 60).padStart(2,'0')}`;
                        return <p className="text-xs text-teal-400 mt-1">= {hms} · {mins} base de cálculo</p>;
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">Número de Episódios</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.episodes}
                        onChange={e => setFormData({...formData, episodes: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                        placeholder="0"
                        data-testid="project-episodes-input"
                      />
                    </div>
                  </div>
                )}

                {/* Equipe (Roteiristas / SRT / Mixadores) */}
                {isAdmin && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="staff-selection">
                    {[
                      { key: 'roteiristas_ids', label: 'Roteiristas (Roteiro)', list: staffUsers.roteiristas, testid: 'roteiristas' },
                      { key: 'srt_ids',         label: 'SRT',                   list: staffUsers.srt,         testid: 'srt' },
                      { key: 'mixadores_ids',   label: 'Mixadores',             list: staffUsers.mixadores,   testid: 'mixadores' },
                    ].map(({ key, label, list, testid }) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
                        <div className="space-y-1 max-h-32 overflow-y-auto bg-slate-700 border border-slate-600 rounded-lg p-2" data-testid={`staff-${testid}`}>
                          {list.length === 0 ? (
                            <p className="text-xs text-slate-500 px-1">Nenhum usuário com esse cargo.</p>
                          ) : list.map(u => {
                            const displayName = u.artist_name || u.name;
                            return (
                              <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-600 rounded cursor-pointer text-sm">
                                <input type="checkbox" className="accent-teal-500 shrink-0"
                                  checked={(formData[key] || []).includes(u.id)}
                                  onChange={(e) => {
                                    const ids = formData[key] || [];
                                    setFormData({
                                      ...formData,
                                      [key]: e.target.checked ? [...ids, u.id] : ids.filter(id => id !== u.id)
                                    });
                                  }}
                                />
                                <span className="text-white truncate flex-1" title={displayName}>{displayName}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Prazo de Entrega</label>
                    <input type="date" value={formData.delivery_deadline} onChange={e => setFormData({...formData, delivery_deadline: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" data-testid="delivery-deadline-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Prazo da Demo</label>
                    <input type="date" value={formData.demo_deadline} onChange={e => setFormData({...formData, demo_deadline: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" data-testid="demo-deadline-input" />
                  </div>
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Data Final de Entrega <span className="text-xs text-slate-500">(não visível para dubladores)</span></label>
                    <input type="date" value={formData.final_deadline} onChange={e => setFormData({...formData, final_deadline: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" data-testid="final-deadline-input" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Material do Projeto</label>
                  <input type="url" value={formData.drive_folder_link} onChange={e => setFormData({...formData, drive_folder_link: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" placeholder="https://drive.google.com/drive/folders/... (link de visualização)" data-testid="drive-folder-input" />
                  <p className="text-[10px] text-slate-500 mt-1">Link clicável que abre o material do projeto (apenas visualização).</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Pasta de Destino</label>
                  <input type="url" value={formData.drive_upload_folder_link} onChange={e => setFormData({...formData, drive_upload_folder_link: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" placeholder="https://drive.google.com/drive/folders/... (onde os uploads serão postados)" data-testid="drive-upload-folder-input" />
                  <p className="text-[10px] text-slate-500 mt-1">Pasta para onde os arquivos enviados pelo botão "Enviar Arquivo" serão postados.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Descrição</label>
                  <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500 h-20" data-testid="project-description-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition-colors">Cancelar</button>
                  <button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-2 rounded-lg transition-colors" data-testid="submit-project-btn">
                    {editingProject ? 'Salvar' : 'Criar Projeto'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Projects;
